import * as React from 'react';
import { ProtectedPageWrapper } from '../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { errorCodes, FileExtensions, FileFolder, rng } from '../../amorg-common';
import { createFolder, deleteFolder, deleteReferenceFile, getFolders, moveFolderFile, updateFolder, uploadReferenceFile } from '../../api/filesClientApi';
import { applicationSettingsClientApi } from '../../api/applicationSettingsClientApi';
import { portalClientApi } from '../../api/portalClientApi';
import { Button } from '../../designSystem/button/Button';
import { Icon } from '../../designSystem/icons/Icons';
import { FileUploadButton } from '../../designSystem/formField/FileUploadButton';
import { toDateTimeString } from '../../utils/date';
import { FileExtension } from '../../amorg-common/DTOs/FileExtension';
import { stringUtil } from '../../amorg-common/util/stringUtil';
import { DeleteConfirmationButton } from '../../designSystem/compositeComponents/DeleteConfirmationButton';
import { AnimatedCollapsibleContent } from '../../designSystem/collapsible/AnimatedCollapsibleContent';
import { Card } from '../../designSystem/Card';
import { Modal, ModalMessage } from '../../designSystem/Modal';
import { TextField } from '../../designSystem/formField/text/TextField';
import { TextAreaField } from '../../designSystem/formField/textArea/TextAreaField';
import { fieldValidation } from '../../amorg-common/modules/fieldValidation/fieldValidation';
import { LinkButton } from '../../designSystem/button/LinkButton';
import { DndContext, DragEndEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import styled from 'styled-components';
import { DragButton } from '../../designSystem/dragndrop/DragButton';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { FileLinkListing } from '../../designSystem/FileLinkListing';


export function DocumentArchiveOverviewPage (): JSX.Element
{
    const [loading, setLoading] = React.useState(true);
    const [folders, setFolders] = React.useState<FileFolder[]>([]);
    const [employeePortalEnabled, setEmployeePortalEnabled] = React.useState(false);
    const [editFolder, setEditFolder] = React.useState<{ id: string, name: string, parentId: string } | null>(null);
    const [shareFile, setShareFile] = React.useState<{ publicFileName: string, privateFileName: string } | null>(null);
    const [modalMessage, setModalMessage] = React.useState('');

    React.useEffect(() =>
    {
        refresh();
    }, []);

    async function refresh ()
    {
        setLoading(true);

        const [folders, applicationSettings] = await Promise.all([
            getFolders(),
            applicationSettingsClientApi.getApplicationSettings()
        ]);
        for(const f of folders)
        {
            f.files.sort((a, b) => a.publicFileName.localeCompare(b.publicFileName));
        }
        folders.sort((a, b) => a.name.localeCompare(b.name));
        setFolders(folders);
        setEmployeePortalEnabled(applicationSettings.employeePortalSettings.enabled);

        setLoading(false);
    }

    async function handleClickCreateFolder ()
    {
        setLoading(true);

        const folders = await getFolders();
        const siblingFolders = folders.filter(f => f.parentFileFolderId === null);

        const newName = stringUtil.generateNextSequenceName(
            'Ny mappe',
            siblingFolders.map(f => f.name)
        );

        await createFolder(rng.getRandomId(), newName, null);

        setLoading(false);
        
        await refresh();
    }

    async function handleClickEditFolder(folderId:string)
    {
        const folder = folders.find(f => f.id === folderId);
        setEditFolder({ id: folderId, name: folder?.name || '', parentId: folder?.parentFileFolderId || '' });
    }

    async function handleFolderUpdated()
    {
        setEditFolder(null);
        await refresh();
    }

    async function handleUnshareFile(file: { publicFileName: string, privateFileName: string }): Promise<void>
    {
        setLoading(true);
        const result = await portalClientApi.unsharePortalResource('DocumentArchiveFile', file.privateFileName);

        if(result.isSuccess)
        {
            await refresh();
            return;
        }

        setLoading(false);
        setModalMessage('Filen kunne ikke fjernes fra deling. Prøv igen senere.');
    }

    async function handleDeleteFile(file: { publicFileName: string, privateFileName: string }): Promise<void>
    {
        setLoading(true);

        try
        {
            await deleteReferenceFile(file.privateFileName);
            await refresh();
        }
        catch
        {
            setLoading(false);
            setModalMessage('Filen kunne ikke slettes. Prøv igen senere.');
        }
    }

    async function handleDragEnd(event:DragEndEvent)
    {
        const destinationFolderId = event.over?.id.toString().substring(7);
        if(!destinationFolderId)
            return;

        const sourceId = event.active.id.toString();

        if(sourceId.startsWith('folder_'))
        {
            const sourceFolderId = sourceId.substring(7);

            if(sourceFolderId === destinationFolderId)
            {
                // If dropped in same folder, do nothing
                return;
            }

            const sourceFolder = folders.find(f => f.id === sourceFolderId);
            if(!sourceFolder)
                return;

            const moveResult = await updateFolder(sourceFolderId, sourceFolder.name, destinationFolderId);
            if(moveResult.isSuccess)
            {
                await refresh();
            }
            else if(moveResult.errorMessage === 'updateFileFolder_name_in_use_by_sibling')
            {
                setModalMessage('Der findes allerede en mappe med det navn');
            }
            else if(moveResult.errorMessage === 'updateFileFolder_cannot_set_parent_to_child_folder')
            {
                setModalMessage('Kan ikke flytte en mappe ind i en af dens undermapper');
            }
        }

        if(sourceId.startsWith('file_'))
        {
            const sourceFilePrivateName = sourceId.substring(5);
            const folder = folders.find(fo => fo.files.some(fi => fi.privateFileName === sourceFilePrivateName));
            if(!folder)                
                return;

            const sourceFolderId = folder.id;

            // If dropped in same folder, do nothing
            if(sourceFolderId === destinationFolderId)
            {
                return;
            }

            const moveResult = await moveFolderFile(sourceFilePrivateName, destinationFolderId);
            if(moveResult.isSuccess)
            {
                await refresh();
            }
            else if(moveResult.errorMessage === 'moveFolderFile_name_in_use_in_target_folder')
            {
                setModalMessage('Der findes allerede en fil med det navn i den valgte mappe');
            }
        }


        event.activatorEvent.stopPropagation();
    }

    const totalFiles = folders.reduce((acc, f) => acc + f.files.length, 0);
    const totalSharedFiles = folders.reduce((acc, f) => acc + f.files.filter(file => file.isShared).length, 0);
    const totalFileSizeKB = folders.reduce((acc, f) => acc + f.files.reduce((acc2, file) => acc2 + file.size, 0), 0) / 1024;
    const totalFileSizeText = totalFileSizeKB >= 1024
        ? `${(totalFileSizeKB / 1024).toFixed(1)} MB`
        : `${totalFileSizeKB.toFixed(1)} KB`;


    return (

        <ProtectedPageWrapper requiredPermissions={['DocumentArchive']} selectedMenuItemKey={'documentarchive'}>
            <SingleColumnLayout
                noDivider
                contentContainer={
                    <Flex flexDirection='column' gap={16}>
                        <LoadingPageOverlay loading={loading} />

                        <PageHeader headerStyle="secondary-gray"
                            headerIcon='data_files_document'
                            headerText='Dokumentarkiv' />

                        <Flex gap={12} style={{ flexWrap: 'wrap' }}>
                            <ArchiveStatCard variant='outlined'>
                                <ArchiveStatValue>{totalFiles}</ArchiveStatValue>
                                <ArchiveStatLabel>Filer</ArchiveStatLabel>
                            </ArchiveStatCard>

                            {employeePortalEnabled &&
                                <ArchiveStatCard variant='outlined'>
                                    <ArchiveStatValue>{totalSharedFiles}</ArchiveStatValue>
                                    <ArchiveStatLabel>Delte filer</ArchiveStatLabel>
                                </ArchiveStatCard>
                            }

                            <ArchiveStatCard variant='outlined'>
                                <ArchiveStatValue>{totalFileSizeText}</ArchiveStatValue>
                                <ArchiveStatLabel>Samlet størrelse</ArchiveStatLabel>
                            </ArchiveStatCard>
                        </Flex>

                        <Card variant="outlined">
                            
                            <Flex justifyContent='flex-end'>
                                <Button variant="outlined"
                                    text='Opret ny mappe'
                                    onClick={handleClickCreateFolder}
                                />
                            </Flex>

                            <Flex flexDirection='column' gap={2}>
                                {folders.length === 0 &&
                                    <div>Ingen mapper oprettet</div>
                                }

                                <DndContext 
                                    onDragEnd={handleDragEnd}>
                                    {folders.filter(fo => !fo.parentFileFolderId).map((folder) => (
                                        <FolderRow key={folder.id}
                                            folder={folder}
                                            allFolders={folders}
                                            employeePortalEnabled={employeePortalEnabled}
                                            onUpdated={refresh}
                                            onEditFolder={handleClickEditFolder}
                                            onShareFile={setShareFile}
                                            onUnshareFile={handleUnshareFile}
                                            onDeleteFile={handleDeleteFile}
                                            onError={() => setModalMessage('Filen kunne ikke uploades. Kontroller at: <ul>'+
                                                '<li>filens størrelse ikke overstiger 50 MB</li>'+
                                                '<li>filen ikke er tom</li>'+
                                                '<li>filens navn (før filtypen) ikke er en tom streng</li>'+
                                                '<li>filens navn er under 255 tegn (inklusive filtypen)</li>'+
                                                '<li>filens navn ikke indeholder specialtegn</li>'+
                                                '<li>filens navn ikke starter eller slutter med et mellemrum</li>'+
                                                '</ul>')} />
                                    ))}
                                </DndContext>
                            </Flex>
                        </Card>

                        <ModalMessage
                            show={modalMessage !== ''}
                            content={<div dangerouslySetInnerHTML={{ __html: modalMessage }} />}
                            onClose={() => setModalMessage('')}
                        />

                        <EditFolderModal 
                            folder={editFolder} 
                            onClose={() => setEditFolder(null)} 
                            onUpdated={handleFolderUpdated} />

                        <FileShareModal
                            file={shareFile}
                            onClose={() => setShareFile(null)}
                            onUpdated={async () => {
                                setShareFile(null);
                                await refresh();
                            }}
                        />

                    </Flex>
                } />

        </ProtectedPageWrapper>
    );
}

type FolderRowProps = {
    folder: FileFolder,
    allFolders: FileFolder[],
    employeePortalEnabled: boolean,
    onEditFolder:(folderId:string) => void,
    onUpdated: () => void,
    onShareFile:(file:{ publicFileName: string, privateFileName: string }) => void,
    onUnshareFile:(file:{ publicFileName: string, privateFileName: string }) => void,
    onDeleteFile:(file:{ publicFileName: string, privateFileName: string }) => void,
    onError: (errorCode:errorCodes) => void,
};
function FolderRow (props: FolderRowProps): JSX.Element
{
    const subFolders = props.allFolders.filter(f => f.parentFileFolderId === props.folder.id);

    const [loading, setLoading] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(props.folder.files.length > 0 || subFolders.length > 0);
    const [showEmptyFileWarning, setShowEmptyFileWarning] = React.useState(false);

    const {isOver, setNodeRef: setDroppableNodeRef} = useDroppable({
        id: `folder_${props.folder.id}`,
    });

    const {attributes, listeners, setNodeRef: setDraggableNodeRef, setActivatorNodeRef, transform} = useDraggable({
        id: `folder_${props.folder.id}`,
    });

    const draggableStyle = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    React.useEffect(() =>
    {
        setIsOpen(props.folder.files.length > 0 || subFolders.length > 0);
    }, [props.folder.files.length, subFolders.length]);

    async function handleClickCreateSubfolder ()
    {
        setLoading(true);
        const folders = await getFolders();
        const childFolders = folders.filter(f => f.parentFileFolderId === props.folder.id);

        const newName = stringUtil.generateNextSequenceName(
            'Ny mappe',
            childFolders.map(f => f.name)
        );

        await createFolder(rng.getRandomId(), newName, props.folder.id);
        setIsOpen(true);
        props.onUpdated();
        setLoading(false);
    }

    async function handleUpload (file: File)
    {
        if (file)
        {
            if(file.size === 0)
                setShowEmptyFileWarning(true);

            setLoading(true);
            const result = await uploadReferenceFile({ type: 'FileFolder', id: props.folder.id }, file);
            if(!result.isSuccess)
            {
                props.onError(result.errorMessage);
                setLoading(false);
            }
            else
            {
                setIsOpen(true);
                props.onUpdated();
                setLoading(false);
            }
        }
    }

    const hasFiles = props.folder.files.length > 0;
    const hasSubFolders = subFolders.length > 0;
    const canOpen = hasFiles || hasSubFolders;
    const canDelete = !hasFiles && !hasSubFolders;

    return (
        <div style={{ margin: '2px', backgroundColor: isOver ? '#f0f0f0' : 'transparent' }}>
            <LoadingPageOverlay loading={loading} />
            
            <ModalMessage
                show={showEmptyFileWarning}
                content={<div>Filen er tom og ikke uploades.</div>}
                onClose={() => setShowEmptyFileWarning(false)}
            />
            
            <Flex alignItems='center' justifyContent='space-between' 
                ref={(node) => {
                    setDroppableNodeRef(node);
                    setDraggableNodeRef(node);
                }} 
                {...attributes} 
                style={draggableStyle}>

                <Flex alignItems='center' gap={4}>
                    
                    <DragButton listeners={listeners as SyntheticListenerMap} setActivatorNodeRef={setActivatorNodeRef} />

                    <Button 
                        variant='text'
                        icon={isOpen ? 'actions_minus' : 'actions_plus'}
                        onClick={() => setIsOpen(!isOpen)}
                        disabled={!canOpen}
                    />

                    <Icon name='data_files_folderOpen' />

                    <span>{props.folder.name}</span>
                </Flex>

                <IconButtonContainer>
                    <Button
                        variant='dimmed'
                        icon={'interface_actions_edit'}
                        tooltipHorizontalAlignment='right'
                        toolTipText='Rediger mappenavn'
                        onClick={() => props.onEditFolder(props.folder.id)}
                    />

                    <DeleteConfirmationButton
                        buttonText=''
                        tooltipHorizontalAlignment='right'
                        tooltipText={canDelete
                            ? 'Slet mappe'
                            : 'Kan ikke slette en mappe med undermapper eller filer'
                        }
                        variant='dimmed'
                        requireKey={false}
                        confirmationHeading='Slet mappen'
                        disabled={!canDelete}
                        confirmationText={`Er du sikker på at du vil slette mappen "${props.folder.name}"?`}
                        onConfirmDelete={async () =>
                        {
                            setLoading(true);
                            await deleteFolder(props.folder.id);
                            props.onUpdated();
                            setLoading(false);
                        }}
                    />

                    <Button
                        variant='dimmed'
                        icon={'data_files_folderCreate'}
                        tooltipHorizontalAlignment='right'
                        toolTipText='Opret undermappe'
                        onClick={handleClickCreateSubfolder}
                    />

                    <FileUploadButton
                        variant='dimmed'
                        inputName='uploaded_file'
                        tooltipHorizontalAlignment='right'
                        toolTipText='Upload fil'
                        fileTypes={FileExtensions as unknown as FileExtension[]}
                        onClickUpload={handleUpload}
                    />
                </IconButtonContainer>

            </Flex>
            <div style={{ margin: '0px 0px 0px 32px' }}>
                <AnimatedCollapsibleContent open={isOpen}>
                    {subFolders.map((f) =>
                        <FolderRow key={f.id} 
                            folder={f}
                            allFolders={props.allFolders}
                            employeePortalEnabled={props.employeePortalEnabled}
                            onUpdated={props.onUpdated}
                            onEditFolder={props.onEditFolder}
                            onShareFile={props.onShareFile}
                            onUnshareFile={props.onUnshareFile}
                            onDeleteFile={props.onDeleteFile}
                            onError={props.onError}
                        />
                    )}
                    {props.folder.files.map((f) => (
                        <FileRow 
                            key={f.privateFileName}
                            file={f} 
                            isShared={f.isShared}
                            employeePortalEnabled={props.employeePortalEnabled}
                                onDeleteFile={props.onDeleteFile}
                            onShareFile={props.onShareFile}
                            onUnshareFile={props.onUnshareFile} />
                    ))}
                </AnimatedCollapsibleContent>
            </div>
        </div>
    );
}

type FileRowProps = {
    file: {
        publicFileName: string,
        privateFileName: string,
        size: number,
        created: Date;
        isShared: boolean;
    },
    isShared: boolean,
    employeePortalEnabled: boolean,
    onDeleteFile:(file:{ publicFileName: string, privateFileName: string }) => void,
    onShareFile:(file:{ publicFileName: string, privateFileName: string }) => void,
    onUnshareFile:(file:{ publicFileName: string, privateFileName: string }) => void,
};
function FileRow (props: FileRowProps): JSX.Element
{
    const {attributes, listeners, setNodeRef, setActivatorNodeRef, transform} = useDraggable({
        id: `file_${props.file.privateFileName}`,
    });

    const draggableStyle = transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : undefined;

    return (
        <>
            <DraggableDiv style={{ justifyContent: 'stretch', alignItems: 'center', margin: '2px', ...draggableStyle }}
                ref={setNodeRef} {...attributes}>
                
                <Flex gap={8} alignItems='center' style={{ flexGrow: 1, minWidth: 0 }} justifyContent='space-between'>

                    <Flex gap={8} alignItems='center' style={{ flexGrow: 1, minWidth: 0 }}>
                        <DragButton listeners={listeners as SyntheticListenerMap} setActivatorNodeRef={setActivatorNodeRef} />

                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                            <FileLinkListing  {...props.file} />
                        </div>
                    </Flex>

                    <Flex alignItems='center' style={{ flexShrink: 0 }}>
                        <div style={{ fontSize:'14px', whiteSpace:'nowrap' }}>
                            {toDateTimeString(props.file.created)}
                        </div>
                        
                        <IconButtonContainer>
                            <DeleteConfirmationButton
                                buttonText=''
                                tooltipHorizontalAlignment='right'
                                tooltipText='Slet fil'
                                variant='dimmed'
                                requireKey={false}
                                confirmationHeading='Slet fil'
                                confirmationText={`Er du sikker på at du vil slette filen "${props.file.publicFileName}"?`}
                                onConfirmDelete={async () => props.onDeleteFile(props.file)}
                            />

                            <LinkButton
                                variant='dimmed'
                                text=''
                                icon='interface_actions_download'
                                href={'/files/' + props.file.privateFileName}
                                download={props.file.publicFileName}
                            />

                            <Button
                                variant={props.isShared ? 'text' : 'dimmed'}
                                icon={props.isShared ? 'interface_actions_share_off' : 'interface_actions_share'}
                                tooltipHorizontalAlignment='right'
                                toolTipText={props.employeePortalEnabled
                                    ? (props.isShared ? 'Filen er delt - fjern deling af filen i medarbejderportalen' : 'Del filen i medarbejderportalen')
                                    : 'Kan ikke dele filen - medarbejderportalen er lukket'}
                                disabled={!props.employeePortalEnabled}
                                onClick={() => props.isShared ? props.onUnshareFile(props.file) : props.onShareFile(props.file)}
                            />
                        </IconButtonContainer>
                    </Flex>
                </Flex>
            </DraggableDiv>
        </>
    );
}



const IconButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    min-width: 180px;
`;

const DraggableDiv = styled.div`
    display: flex;
`;

const ArchiveStatCard = styled(Card)`
    min-width: 140px;
    padding: 12px 16px;
`;

const ArchiveStatValue = styled.div`
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
`;

const ArchiveStatLabel = styled.div`
    font-size: 13px;
    color: ${props => props.theme.colors.text.state.info};
`;

type EditFolderModalProps = 
{
    folder:{id:string, name:string, parentId:string } | null,
    onUpdated:() => void,
    onClose:() => void,
}
function EditFolderModal(props:EditFolderModalProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [name, setName] = React.useState(props.folder?.name || ''); 
    const [error, setError] = React.useState('');

    React.useEffect(() =>
    {
        setName(props.folder ? props.folder.name : '');
        setError('');
    }, [props.folder]);

    async function handleClickSave()
    {
        if(!props.folder)
            return;

        if(!fieldValidation.fileFolder.name(name))
        {
            setError('Ugyldigt mappenavn - skal være 3-100 standardtegn og må ikke starte eller slutte med mellemrum');
            return;
        }

        setLoading(true);
        const result = await updateFolder(props.folder.id, name, props.folder.parentId || null);

        if(result.isSuccess)
        {
            props.onUpdated();
        }
        else
        {
            if(result.errorMessage === 'updateFileFolder_name_in_use_by_sibling')
            {
                setError('Mappenavnet er allerede i brug i samme mappe');
            }
            else
            {
                setError('Der skete en uventet fejl, prøv igen senere');
            }
        }

        setLoading(false);
    }

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            <Modal
                title='Rediger mappe'
                showModal={props.folder !== null}
                width='400px'
                onCancel={props.onClose}
                content={
                    <Flex flexDirection='column' gap={16}>
                        <TextField
                            label='Mappe navn'
                            value={name}
                            onChange={(v) => { setName(v); }}
                            errorMessage={error}
                        />
                        <Button
                            variant='filled'
                            text='Gem'
                            onClick={handleClickSave}
                        />
                    </Flex>
                }
            />
        </>
    );
}

type FileShareModalProps = {
    file:{
        publicFileName: string,
        privateFileName: string,
    } | null,
    onUpdated:() => void,
    onClose:() => void,
}
function FileShareModal(props:FileShareModalProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);
    const [shareNote, setShareNote] = React.useState('');
    const [shareError, setShareError] = React.useState('');

    React.useEffect(() =>
    {
        if(props.file)
        {
            setShareNote('');
            setShareError('');
        }
    }, [props.file]);

    async function handleShareFile()
    {
        if(!props.file)
            return;

        setLoading(true);
        const result = await portalClientApi.sharePortalResource('DocumentArchiveFile', props.file.privateFileName, { note: shareNote.trim() });

        if(result.isSuccess)
        {
            await props.onUpdated();
            setLoading(false);
            return;
        }

        if(result.errorMessage === 'sharePortalResource_resource_already_shared')
        {
            setShareError('Filen er allerede delt.');
            setLoading(false);
            return;
        }

        setShareError('Filen kunne ikke deles. Prøv igen senere.');
        setLoading(false);
    }

    return (
        <>
            <Modal
                title='Del fil i medarbejderportalen'
                showModal={props.file !== null}
                width='500px'
                onCancel={props.onClose}
                content={
                    <Flex flexDirection='column' gap={16}>
                        <LoadingPageOverlay loading={loading} />
                        <TextAreaField
                            label='Note om deling til medarbejderne (valgfri)'
                            value={shareNote}
                            minRows={6}
                            onChange={(value) => {
                                setShareNote(value);
                                if(shareError)
                                    setShareError('');
                            }}
                            errorMessage={shareError}
                        />

                        <Button
                            variant='filled'
                            text='Del'
                            onClick={handleShareFile}
                        />
                    </Flex>
                }
            />
        </>
    );
}